import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Plus, 
  Building2, 
  Trash2,
  Pencil,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Key
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const CompaniesPage = ({ user }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    unique_code: ""
  });

  const canEdit = user?.role === "superuser" || user?.role === "administrator";

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Gagal memuat data perusahaan");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      email: "",
      phone: "",
      unique_code: ""
    });
    setSelectedCompany(null);
  };

  const handleCreate = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error("Nama perusahaan wajib diisi");
        return;
      }
      
      await axios.post(`${API}/companies`, formData);
      toast.success("Perusahaan berhasil ditambahkan");
      setIsCreateOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      console.error("Error creating company:", error);
      toast.error(error.response?.data?.detail || "Gagal menambahkan perusahaan");
    }
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name || "",
      address: company.address || "",
      email: company.email || "",
      phone: company.phone || "",
      unique_code: company.unique_code || ""
    });
    setIsEditOpen(true);
  };

  const handleEdit = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error("Nama perusahaan wajib diisi");
        return;
      }
      
      await axios.put(`${API}/companies/${selectedCompany.company_id}`, formData);
      toast.success("Perusahaan berhasil diupdate");
      setIsEditOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      console.error("Error updating company:", error);
      toast.error(error.response?.data?.detail || "Gagal mengupdate perusahaan");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/companies/${selectedCompany.company_id}`);
      toast.success("Perusahaan berhasil dihapus");
      setIsDeleteOpen(false);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (error) {
      console.error("Error deleting company:", error);
      toast.error(error.response?.data?.detail || "Gagal menghapus perusahaan");
    }
  };

  return (
    <Layout user={user}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Perusahaan Asuransi</h1>
            <p className="page-subtitle">Kelola daftar perusahaan asuransi partner</p>
          </div>
          {canEdit && (
            <Button 
              className="btn-accent"
              onClick={() => {
                resetForm();
                setIsCreateOpen(true);
              }}
              data-testid="add-company-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Perusahaan
            </Button>
          )}
        </div>

        {/* Companies Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : companies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <Card 
                key={company.company_id} 
                className="card-hover"
                data-testid={`company-card-${company.company_id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4 overflow-hidden">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h3 className="font-semibold text-slate-900 truncate">{company.name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        {new Date(company.created_at).toLocaleDateString('id-ID')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Company Details */}
                  <div className="space-y-2 mb-4">
                    {company.unique_code && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Key className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <span className="font-mono bg-amber-50 px-2 py-0.5 rounded text-amber-700">
                          {company.unique_code}
                        </span>
                      </div>
                    )}
                    {company.address && (
                      <div className="flex items-start gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">{company.address}</span>
                      </div>
                    )}
                    {company.email && (
                      <div className="flex items-center gap-2 text-sm text-slate-600 overflow-hidden">
                        <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <a href={`mailto:${company.email}`} className="text-blue-600 hover:underline truncate block">
                          {company.email}
                        </a>
                      </div>
                    )}
                    {company.phone && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <a href={`tel:${company.phone}`} className="hover:text-blue-600">
                          {company.phone}
                        </a>
                      </div>
                    )}
                    {!company.address && !company.email && !company.phone && !company.unique_code && (
                      <p className="text-sm text-slate-400 italic">Belum ada detail kontak</p>
                    )}
                  </div>

                  {/* Actions */}
                  {canEdit && (
                    <div className="flex gap-2 pt-4 border-t border-slate-100">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditDialog(company)}
                        data-testid={`edit-company-${company.company_id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setSelectedCompany(company);
                          setIsDeleteOpen(true);
                        }}
                        data-testid={`delete-company-${company.company_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16">
              <div className="empty-state">
                <Building2 className="empty-state-icon" />
                <p className="empty-state-title">Belum ada perusahaan</p>
                <p className="empty-state-description">
                  Tambahkan perusahaan asuransi untuk memulai
                </p>
                {canEdit && (
                  <Button 
                    className="btn-accent"
                    onClick={() => {
                      resetForm();
                      setIsCreateOpen(true);
                    }}
                    data-testid="add-first-company-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah Perusahaan Pertama
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Tambah Perusahaan Asuransi</DialogTitle>
              <DialogDescription>
                Masukkan data perusahaan asuransi baru
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Nama Perusahaan *</Label>
                <Input
                  data-testid="input-company-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Masukkan nama perusahaan"
                />
              </div>
              <div>
                <Label className="form-label">Kode Unik Registrasi</Label>
                <Input
                  data-testid="input-company-unique-code"
                  value={formData.unique_code}
                  onChange={(e) => setFormData({ ...formData, unique_code: e.target.value })}
                  placeholder="Contoh: ASURANSI123"
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Kode ini digunakan untuk verifikasi saat guest mendaftar
                </p>
              </div>
              <div>
                <Label className="form-label">Alamat</Label>
                <Textarea
                  data-testid="input-company-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Masukkan alamat perusahaan"
                  rows={3}
                />
              </div>
              <div>
                <Label className="form-label">Email</Label>
                <Input
                  data-testid="input-company-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@perusahaan.com"
                />
              </div>
              <div>
                <Label className="form-label">No. Telepon</Label>
                <Input
                  data-testid="input-company-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="021-xxxxxxx"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsCreateOpen(false);
                resetForm();
              }}>
                Batal
              </Button>
              <Button className="btn-accent" onClick={handleCreate} data-testid="submit-company">
                Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Perusahaan Asuransi</DialogTitle>
              <DialogDescription>
                Perbarui data perusahaan asuransi
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Nama Perusahaan *</Label>
                <Input
                  data-testid="edit-company-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Masukkan nama perusahaan"
                />
              </div>
              <div>
                <Label className="form-label">Kode Unik Registrasi</Label>
                <Input
                  data-testid="edit-company-unique-code"
                  value={formData.unique_code}
                  onChange={(e) => setFormData({ ...formData, unique_code: e.target.value })}
                  placeholder="Contoh: ASURANSI123"
                  className="font-mono"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Kode ini digunakan untuk verifikasi saat guest mendaftar
                </p>
              </div>
              <div>
                <Label className="form-label">Alamat</Label>
                <Textarea
                  data-testid="edit-company-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Masukkan alamat perusahaan"
                  rows={3}
                />
              </div>
              <div>
                <Label className="form-label">Email</Label>
                <Input
                  data-testid="edit-company-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@perusahaan.com"
                />
              </div>
              <div>
                <Label className="form-label">No. Telepon</Label>
                <Input
                  data-testid="edit-company-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="021-xxxxxxx"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditOpen(false);
                resetForm();
              }}>
                Batal
              </Button>
              <Button className="btn-accent" onClick={handleEdit} data-testid="submit-edit-company">
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus Perusahaan?</AlertDialogTitle>
              <AlertDialogDescription>
                Apakah Anda yakin ingin menghapus "{selectedCompany?.name}"? 
                Perusahaan tidak dapat dihapus jika masih memiliki klaim yang terkait.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-company"
              >
                Hapus
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default CompaniesPage;
